import { ConsolidateCameraMonitoring } from '@EcamModel/model/CameraOverview';

export interface ColumnStyleConfig {
    minWidth?: string;
    stickyMinWidth?: string;
    width?: number;
    stickyWidth?: number;
    align?: 'left' | 'center' | 'right';
    priority?: 'high' | 'medium' | 'low';
    category?: 'primary' | 'secondary' | 'tertiary';
}

const createColumnConfig = (config: ColumnStyleConfig = {}): ColumnStyleConfig => ({
    minWidth: '50px',
    stickyMinWidth: '50px',
    width: 50,
    stickyWidth: 50,
    align: 'center',
    priority: 'low',
    category: 'tertiary',
    ...config,
});

const presets = {
    primary: { align: 'center' as const, priority: 'high' as const, category: 'primary' as const },
    secondary: { align: 'center' as const, priority: 'medium' as const, category: 'secondary' as const },
    tertiary: { align: 'center' as const, priority: 'low' as const, category: 'tertiary' as const },
    wide: { minWidth: '200px', stickyMinWidth: '200px', width: 200, stickyWidth: 200 },
    medium: { minWidth: '100px', stickyMinWidth: '50px', width: 100, stickyWidth: 50 },
    narrow: { minWidth: '80px', stickyMinWidth: '50px', width: 80, stickyWidth: 50 },
};

export const columnStyleConfigs: { [key in keyof ConsolidateCameraMonitoring]: ColumnStyleConfig } = {
    LocationName: createColumnConfig({ ...presets.primary, ...presets.wide }),
    CameraName: createColumnConfig({ ...presets.primary, ...presets.wide }),
    ZoneName: createColumnConfig({
        ...presets.secondary,
        minWidth: '150px',
        stickyMinWidth: '150px',
        width: 150,
        stickyWidth: 150,
    }),

    CameraStatus: createColumnConfig({ ...presets.primary, ...presets.medium }),
    CameraTaskOverDueCount: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    LastContact: createColumnConfig({
        ...presets.primary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    AvgDailyWorkedHours: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    AvgDailyPhotosTaken: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    TotalPhotosTaken: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    PotentialContraventions: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    InprogressContraventions: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    CancelledContraventions: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    ApprovedContraventions: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    PotentialRate: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    ApprovalRate: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    CancellationRate: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    PredictedBatteryRunOutDate: createColumnConfig({
        ...presets.primary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    AvgBattery: createColumnConfig({ ...presets.primary, ...presets.narrow }),
    CurrentBattery: createColumnConfig({ ...presets.primary, ...presets.narrow }),

    CameraId: createColumnConfig({ ...presets.secondary, ...presets.narrow }),
    CameraDeployDate: createColumnConfig({
        ...presets.secondary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    CameraService: createColumnConfig({
        ...presets.secondary,
        minWidth: '70px',
        stickyMinWidth: '85px',
        width: 70,
        stickyWidth: 85,
    }),
    CameraTaskCount: createColumnConfig({
        ...presets.secondary,
        minWidth: '60px',
        stickyMinWidth: '50px',
        width: 60,
        stickyWidth: 50,
    }),
    IsVerificationsEnabled: createColumnConfig({
        ...presets.secondary,
        minWidth: '60px',
        stickyMinWidth: '50px',
        width: 60,
        stickyWidth: 50,
    }),
    LatestPhotoTime: createColumnConfig({
        ...presets.secondary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    LastWifiStatus: createColumnConfig({ ...presets.secondary, ...presets.medium }),
    UploadMode: createColumnConfig({ ...presets.secondary, ...presets.medium }),
    Frequency: createColumnConfig({ ...presets.secondary, ...presets.medium }),
    NetworkType: createColumnConfig({ ...presets.secondary, ...presets.medium }),
    ConnectStatus: createColumnConfig({ ...presets.secondary, ...presets.medium }),
    SignalStrength: createColumnConfig({ ...presets.secondary, ...presets.narrow }),
    Pingable: createColumnConfig({ ...presets.secondary, ...presets.narrow }),
    ImagesLeft: createColumnConfig({ ...presets.secondary, ...presets.narrow }),
    PurposeType: createColumnConfig({ ...presets.secondary, ...presets.medium }),
    CameraPowerSupply: createColumnConfig({ ...presets.secondary, ...presets.medium }),
    TakenPhotosToday: createColumnConfig({ ...presets.secondary, ...presets.narrow }),

    LocationId: createColumnConfig({
        ...presets.tertiary,
        minWidth: '60px',
        stickyMinWidth: '50px',
        width: 60,
        stickyWidth: 50,
    }),
    ZoneId: createColumnConfig({
        ...presets.tertiary,
        minWidth: '60px',
        stickyMinWidth: '50px',
        width: 60,
        stickyWidth: 50,
    }),
    CameraDeleted: createColumnConfig({
        ...presets.tertiary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    CameraNoteCount: createColumnConfig({
        ...presets.tertiary,
        minWidth: '60px',
        stickyMinWidth: '50px',
        width: 60,
        stickyWidth: 50,
    }),
    IP: createColumnConfig({
        ...presets.tertiary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    TimeZone: createColumnConfig({ ...presets.tertiary, ...presets.medium }),
    DevMac: createColumnConfig({
        ...presets.tertiary,
        minWidth: '150px',
        stickyMinWidth: '50px',
        width: 150,
        stickyWidth: 50,
    }),
    CameraVersion: createColumnConfig({ ...presets.tertiary, ...presets.medium }),
    ServerTime: createColumnConfig({
        ...presets.tertiary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    CameraTime: createColumnConfig({
        ...presets.tertiary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    DifferentTime: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    DifferentMinute: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    SetTimeAutomatically: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    CaptureTimeout: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    RadarSensitivity: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    LowSpeedLimit: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    Indicator: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    PostType: createColumnConfig({ ...presets.tertiary, ...presets.medium }),
    PostCaptureInterval: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    PostCaptureNum: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    Firmware: createColumnConfig({
        ...presets.tertiary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    CaptureInterval: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    Apn: createColumnConfig({ ...presets.tertiary, ...presets.medium }),
    Iccid: createColumnConfig({
        ...presets.tertiary,
        minWidth: '150px',
        stickyMinWidth: '50px',
        width: 150,
        stickyWidth: 50,
    }),
    SimStatus: createColumnConfig({ ...presets.tertiary, ...presets.medium }),
    CameraPriority: createColumnConfig({
        ...presets.tertiary,
        minWidth: '80px',
        stickyMinWidth: '85px',
        width: 80,
        stickyWidth: 85,
    }),
    CameraSDUsed: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    CameraSDFreeSpace: createColumnConfig({ ...presets.tertiary, ...presets.narrow }),
    CameraSDCapacity: createColumnConfig({ ...presets.tertiary, ...presets.medium }),
    LastSwitchUploadModeAt: createColumnConfig({
        ...presets.tertiary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
    PTZLastContact: createColumnConfig({
        ...presets.tertiary,
        minWidth: '120px',
        stickyMinWidth: '50px',
        width: 120,
        stickyWidth: 50,
    }),
};

export const getColumnConfig = <T>(columnId: keyof T): ColumnStyleConfig | null => {
    return (columnStyleConfigs as any)[columnId] || null;
};

export const getColumnWidth = <T>(columnId: keyof T, isSticky: boolean = false): number => {
    const config = getColumnConfig(columnId);
    return config ? (isSticky ? config.stickyWidth || 50 : config.width || 50) : 50;
};

export const getColumnMinWidth = <T>(columnId: keyof T, isSticky: boolean = false): string => {
    const config = getColumnConfig(columnId);
    return config ? (isSticky ? config.stickyMinWidth || '50px' : config.minWidth || '50px') : '50px';
};

export const getColumnAlignment = <T>(
    columnId: keyof T,
    alignmentFields?: { [key in keyof T]?: 'inherit' | 'center' | 'left' | 'justify' | 'right' | undefined },
    leftAlignFields?: Array<keyof T>
): 'inherit' | 'center' | 'left' | 'justify' | 'right' | undefined => {
    const config = getColumnConfig(columnId);
    return config?.align || alignmentFields?.[columnId] || (leftAlignFields?.includes(columnId) ? 'left' : 'center');
};
